import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Skeleton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Alert,
  Badge,
  Rating,
  Link,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { candidatesAPI, exportAPI } from "../services/api";
import axios from "axios";

// Add this helper function near the top of the file, after other parse helpers
const safeParseOverlappingSkills = (data) => {
  if (!data) return [];
  try {
    if (typeof data === "string") {
      // Replace single quotes with double quotes for JSON.parse compatibility
      const normalized = data.replace(/'/g, '"');
      return JSON.parse(normalized);
    }
    if (Array.isArray(data)) return data;
    return [];
  } catch {
    return [];
  }
};

// Candidate Card Component
const CandidateCard = ({
  candidate,
  onSelect,
  onEdit,
  onDelete,
  selected,
  onCardClick,
}) => {
  console.log("Candidate", candidate);
  const [showDetails, setShowDetails] = useState(false);
  const [detailedCandidate, setDetailedCandidate] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const getScoreColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "error";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Poor";
  };

  // Parse skills and overlapping skills
  const parseSkills = (skillsString) => {
    try {
      if (typeof skillsString === "string") {
        return JSON.parse(skillsString);
      }
      return skillsString || [];
    } catch (e) {
      return [];
    }
  };

  const handleCardClick = async () => {
    setIsLoadingDetails(true);
    try {
      const response = await axios.get(
        `http://20.127.236.209:8000/candidates/get-candidates-info/${candidate.id}`
      );
      setDetailedCandidate(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error("Error fetching candidate details:", error);
      // Fallback to basic candidate data if API fails
      setDetailedCandidate(candidate);
      setShowDetails(true);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const overlappingSkills = detailedCandidate
    ? safeParseOverlappingSkills(detailedCandidate.overlapping_skills)
    : [];

  const skills = detailedCandidate ? parseSkills(detailedCandidate.skills) : [];

  console.log("Show Details", showDetails);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          height: 160,
          width: 300,
          position: "relative",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          transition: "all 0.3s ease",
          boxSizing: "border-box",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
          },
        }}
        onClick={handleCardClick}
      >
        {/* Loader overlay when loading details */}
        {isLoadingDetails && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(255,255,255,0.7)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <CardContent
          sx={{
            pt: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            pb: 1,
          }}
        >
          {/* Top Row: Avatar, Name, Headline */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
              {candidate.full_name?.charAt(0) || "C"}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                  lineHeight: 1.1,
                }}
              >
                {candidate.full_name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: 1.2,
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                }}
              >
                {candidate.headline}
              </Typography>
            </Box>
          </Box>

          {/* Score and AI Score */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mt: 2,
            }}
          >
            {/* Match Score Progress Bar */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  Match Score
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color:
                      getScoreColor(candidate.score) === "success"
                        ? "success.main"
                        : getScoreColor(candidate.score) === "warning"
                        ? "warning.main"
                        : "error.main",
                  }}
                >
                  {candidate.score?.toFixed(1) || 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={candidate.score || 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor:
                      getScoreColor(candidate.score) === "success"
                        ? "success.main"
                        : getScoreColor(candidate.score) === "warning"
                        ? "warning.main"
                        : "error.main",
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {/* AI Score Progress Bar */}
            {typeof candidate.ai_score === "number" && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    AI Assessment
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    {candidate.ai_score}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={candidate.ai_score}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "grey.200",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "primary.main",
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>

        {/* Details Dialog */}
        <Dialog
          open={showDetails}
          onClose={(event, reason) => {
            console.log("Dialog onClose triggered, reason:", reason);
            setShowDetails(false);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ position: "relative", pr: 5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: "primary.main" }}>
                {candidate.full_name?.charAt(0) || "C"}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {candidate.full_name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {detailedCandidate?.headline ||
                    detailedCandidate?.job_description ||
                    candidate.headline}
                </Typography>
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={(e) => {
                e.stopPropagation();
                console.log(
                  "Close button clicked, setting showDetails to false"
                );
                setShowDetails(false);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {isLoadingDetails ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <Skeleton variant="rectangular" width="100%" height={200} />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Scores Section */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Assessment Scores
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          color="primary"
                          sx={{ fontWeight: 700 }}
                        >
                          {candidate.score?.toFixed(1) || 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Match Score
                        </Typography>
                      </Box>
                      {typeof candidate.ai_score === "number" && (
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="h4"
                            color="secondary"
                            sx={{ fontWeight: 700 }}
                          >
                            {candidate.ai_score}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            AI Assessment
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* About Section */}
                {detailedCandidate?.about && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      About
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {detailedCandidate.about}
                    </Typography>
                  </Grid>
                )}

                {/* Overlapping Skills Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Overlapping Skills
                  </Typography>
                  {overlappingSkills.length > 0 ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {overlappingSkills.map((skill, index) => (
                        <Paper
                          key={index}
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Chip
                            label={skill.skill}
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Score: <b>{skill.score}%</b>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Exp: <b>{skill.experience_years} yrs</b>
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            {skill.reason}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No overlapping skills found.
                    </Typography>
                  )}
                </Grid>

                {/* Match Reasons Section */}
                {detailedCandidate?.match_reasons && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Match Reasons
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {detailedCandidate.match_reasons}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                )}

                {/* All Skills */}
                {skills.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      All Skills
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {skills.map((skillGroup, index) => (
                        <React.Fragment key={index}>
                          {typeof skillGroup === "string" &&
                            skillGroup
                              .split(" · ")
                              .map((skill, skillIndex) => (
                                <Chip
                                  key={`${index}-${skillIndex}`}
                                  label={skill}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: "0.75rem" }}
                                />
                              ))}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Experience */}
                {detailedCandidate?.experience && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Experience
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {(() => {
                        try {
                          const experience = JSON.parse(
                            detailedCandidate.experience
                          );
                          return experience.map((exp, index) => (
                            <Paper
                              key={index}
                              sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {exp.title}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="primary"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {exp.company}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={exp.duration}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {exp.description}
                              </Typography>
                            </Paper>
                          ));
                        } catch (e) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              {detailedCandidate.experience}
                            </Typography>
                          );
                        }
                      })()}
                    </Box>
                  </Grid>
                )}

                {/* Education */}
                {detailedCandidate?.education && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Education
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {(() => {
                        try {
                          const education = JSON.parse(
                            detailedCandidate.education
                          );
                          return education.map((edu, index) => (
                            <Paper
                              key={index}
                              sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                {edu.degree}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={{ fontWeight: 500 }}
                              >
                                {edu.school}
                              </Typography>
                              {edu.activities && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 1 }}
                                >
                                  {edu.activities}
                                </Typography>
                              )}
                            </Paper>
                          ));
                        } catch (e) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              {detailedCandidate.education}
                            </Typography>
                          );
                        }
                      })()}
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              startIcon={<LinkedInIcon />}
              onClick={() =>
                window.open(detailedCandidate?.linkedin_url, "_blank")
              }
              disabled={!detailedCandidate?.linkedin_url}
            >
              View LinkedIn Profile
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </motion.div>
  );
};

// Filters Component
const Filters = ({ filters, onFilterChange, onClearFilters }) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearFilters}
          >
            Clear All
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search by name"
              value={filters.search}
              onChange={(e) => onFilterChange("search", e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Score Range</InputLabel>
              <Select
                value={filters.scoreRange}
                onChange={(e) => onFilterChange("scoreRange", e.target.value)}
              >
                <MenuItem value="">All Scores</MenuItem>
                <MenuItem value="80-100">80-100% (Excellent)</MenuItem>
                <MenuItem value="60-79">60-79% (Good)</MenuItem>
                <MenuItem value="40-59">40-59% (Fair)</MenuItem>
                <MenuItem value="0-39">0-39% (Poor)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={filters.location}
                onChange={(e) => onFilterChange("location", e.target.value)}
              >
                <MenuItem value="">All Locations</MenuItem>
                <MenuItem value="United States">United States</MenuItem>
                <MenuItem value="Remote">Remote</MenuItem>
                <MenuItem value="New York">New York</MenuItem>
                <MenuItem value="California">California</MenuItem>
                <MenuItem value="Texas">Texas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => onFilterChange("status", e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="selected">Selected</MenuItem>
                <MenuItem value="not_selected">Not Selected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const Candidates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    scoreRange: "",
    location: "",
    status: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'table'

  // Candidates API state
  const [candidates, setCandidates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const user_id = String(localStorage.getItem("userId") || "123");

  const { data: candidatesData, isLoading: isLoadingData, error: apiError, refetch } = useQuery(
    "candidates",
    () => candidatesAPI.list(filters),
    {
      onSuccess: (data) => {
        setCandidates(data);
        setIsLoading(false);
      },
      onError: (err) => {
        setError(err);
        setIsLoading(false);
      },
    }
  );

  const updateCandidateMutation = useMutation(
    (data) => candidatesAPI.update(data.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("candidates");
      },
    }
  );

  const deleteCandidateMutation = useMutation(
    (id) => candidatesAPI.delete(id),
    {
      onSuccess: (data, id) => {
        queryClient.invalidateQueries("candidates");
        setSelectedCandidates(selectedCandidates.filter((c) => c !== id));
      },
    }
  );

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      scoreRange: "",
      location: "",
      status: "",
    });
    setPage(0);
  };

  const handleSelectCandidate = (id, selected) => {
    if (selected) {
      setSelectedCandidates((prev) => [...prev, id]);
    } else {
      setSelectedCandidates((prev) => prev.filter((c) => c !== id));
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      setSelectedCandidates(candidatesData?.candidates?.map((c) => c.id) || []);
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleEditCandidate = (candidate) => {
    // Implement edit functionality
    console.log("Edit candidate:", candidate);
  };

  const handleDeleteCandidate = (id) => {
    if (window.confirm("Are you sure you want to delete this candidate?")) {
      deleteCandidateMutation.mutate(id);
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportAPI.exportCandidates(selectedCandidates);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "candidates.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const refreshData = () => {
    queryClient.invalidateQueries("candidates");
    refetch();
  };

  // Filter candidates based on search and score range
  const filteredCandidates =
    candidatesData?.candidates?.filter((candidate) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (
          !candidate.full_name?.toLowerCase().includes(searchLower) &&
          !candidate.headline?.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      // Score range filter
      if (filters.scoreRange) {
        const [min, max] = filters.scoreRange.split("-").map(Number);
        const score = candidate.score || 0;
        if (score < min || score > max) return false;
      }

      return true;
    }) || [];

  const paginatedCandidates = filteredCandidates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Loader logic: show loader if loading
  if (isLoading || isLoadingData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || apiError) {
    return <Alert severity="error">Failed to load candidates.</Alert>;
  }

  return (
    <Box sx={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Scrollable Container - Header and Content together */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "24px 24px 0 24px",
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Candidates
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Browse and manage sourced candidates
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={refreshData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!candidatesData?.candidates?.length}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={{ padding: "24px" }}>
      {/* Filters */}
      <Filters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Results Summary */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {filteredCandidates.length} candidates
          {selectedCandidates.length > 0 &&
            ` (${selectedCandidates.length} selected)`}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant={viewMode === "grid" ? "contained" : "outlined"}
            onClick={() => setViewMode("grid")}
          >
            Grid
          </Button>
          <Button
            size="small"
            variant={viewMode === "table" ? "contained" : "outlined"}
            onClick={() => setViewMode("table")}
          >
            Table
          </Button>
        </Box>
      </Box>

      {/* Candidates Grid/Table */}
      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          ))}
        </Grid>
      ) : filteredCandidates.length > 0 ? (
        <>
          {viewMode === "grid" ? (
            <Grid container spacing={3}>
              <AnimatePresence>
                {paginatedCandidates.map((candidate) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={candidate.id}>
                    <CandidateCard
                      candidate={candidate}
                      selected={selectedCandidates.includes(candidate.id)}
                      onSelect={handleSelectCandidate}
                      onEdit={handleEditCandidate}
                      onDelete={handleDeleteCandidate}
                    />
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          selectedCandidates.length ===
                          filteredCandidates.length
                        }
                        indeterminate={
                          selectedCandidates.length > 0 &&
                          selectedCandidates.length < filteredCandidates.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Headline</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCandidates.map((candidate) => (
                    <TableRow key={candidate.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCandidates.includes(candidate.id)}
                          onChange={(e) =>
                            handleSelectCandidate(
                              candidate.id,
                              e.target.checked
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {candidate.full_name?.charAt(0) || "C"}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {candidate.full_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{candidate.headline}</TableCell>
                      <TableCell>{candidate.location}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${candidate.score || 0}%`}
                          size="small"
                          color={
                            candidate.score >= 80
                              ? "success"
                              : candidate.score >= 60
                              ? "warning"
                              : "error"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            candidate.selected ? "Selected" : "Not Selected"
                          }
                          size="small"
                          color={candidate.selected ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(candidate.linkedin_url, "_blank")
                            }
                          >
                            <LinkedInIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditCandidate(candidate)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteCandidate(candidate.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <TablePagination
              component="div"
              count={filteredCandidates.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[12, 24, 48]}
            />
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              bgcolor: "grey.300",
            }}
          ></Avatar>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Candidates Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filters.search ||
            filters.scoreRange ||
            filters.location ||
            filters.status
              ? "Try adjusting your filters to see more results."
              : "Start a harvest pipeline to source candidates."}
          </Typography>
          {!filters.search &&
            !filters.scoreRange &&
            !filters.location &&
            !filters.status && (
              <Button
                variant="contained"
                onClick={() => navigate("/candidate-sourcing")}
              >
                Start Harvesting
              </Button>
            )}
        </Box>
      )}
        </Box>
      </Box>
    </Box>
  );
};

export default Candidates;
